/**
 * Voterra brand graphics, ported from the marketing site (voterra.io) for
 * subdued use inside the app.
 *
 * Three pieces, and a deliberate split in where each may appear:
 *
 * - `SignalCellMark` — the "Signal Cell": a choropleth hexagon shaded on the
 *   propensity ramp with one lit corner node (marketing-site/src/components/
 *   Mark.astro). The PRODUCT's mark, so it sits next to product-owned chrome
 *   (login card, header brand) — never inside tenant data surfaces.
 * - `HexFieldBackdrop` — a faint tiling field of hexagon outlines for large
 *   quiet regions (login page, Home greeting). Drawn in `currentColor` at very
 *   low opacity so it inherits the theme's foreground and works in both
 *   modes; a radial mask fades it out so it reads as texture, not wallpaper.
 * - `PropensityStrip` — the five-stop cold→hot ramp from the marketing site's
 *   signature bars. Fixed brand colors on product surfaces (login); in-app
 *   accents stay on the tenant's `--accent` tokens.
 *
 * All three are decoration: aria-hidden, pointer-events-none, zero layout
 * cost (absolutely positioned or fixed-size inline SVG). No animation — the
 * subdued treatment is the point, and it sidesteps reduced-motion concerns.
 */
import React, { useId } from 'react';

/** The five-stop cold→hot propensity ramp (marketing `.hero-bar`). */
export const PROPENSITY_RAMP =
  'linear-gradient(90deg,#161E2A,#2E3644,#5C3A36,#B03A2B,#E8482B)';

const HEX_POINTS = '60,8 105,34 105,86 60,112 15,86 15,34';

interface MarkProps {
  size?: number;
  className?: string;
}

/** The Voterra "Signal Cell" mark — hexagon on the propensity ramp, one lit corner. */
export const SignalCellMark: React.FC<MarkProps> = ({ size = 20, className }) => {
  // Unique gradient id per render so several marks can coexist in one document.
  const uid = useId();
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 120 120"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id={uid} x1="0" y1="1" x2="1" y2="0">
          <stop offset="0" stopColor="#2A3340" />
          <stop offset="0.55" stopColor="#7E3E32" />
          <stop offset="1" stopColor="#E8482B" />
        </linearGradient>
      </defs>
      <polygon points={HEX_POINTS} fill={`url(#${uid})`} />
      <circle cx="105" cy="34" r="11" fill="#E8482B" opacity="0.3" />
      <circle cx="105" cy="34" r="6.5" fill="#F0563A" />
    </svg>
  );
};

interface HexFieldProps {
  /** Extra classes on the positioned wrapper (defaults cover the parent). */
  className?: string;
  /** Where the field is densest before the mask fades it out. */
  origin?: 'top-right' | 'center';
}

/**
 * A faint field of hexagon outlines behind content. Parent must be
 * `relative`; content above it needs `relative` (or a z-index) too.
 */
export const HexFieldBackdrop: React.FC<HexFieldProps> = ({
  className = '',
  origin = 'top-right',
}) => {
  const uid = useId();
  const mask =
    origin === 'center'
      ? 'radial-gradient(80% 80% at 50% 40%, black 0%, transparent 100%)'
      : 'radial-gradient(70% 90% at 85% 0%, black 0%, transparent 100%)';
  return (
    <div
      className={`absolute inset-0 overflow-hidden pointer-events-none select-none text-fg-primary ${className}`}
      style={{ maskImage: mask, WebkitMaskImage: mask }}
      aria-hidden="true"
    >
      <svg width="100%" height="100%" className="opacity-[0.05]">
        <defs>
          {/* One hexagon per 56×97 tile, offset half a tile for the honeycomb. */}
          <pattern id={uid} width="56" height="97" patternUnits="userSpaceOnUse">
            <polygon
              points="28,2 54,17 54,47 28,62 2,47 2,17"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            />
            <polygon
              points="56,50.5 82,65.5 82,95.5 56,110.5 30,95.5 30,65.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            />
            <polygon
              points="0,50.5 26,65.5 26,95.5 0,110.5 -26,95.5 -26,65.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill={`url(#${uid})`} />
      </svg>
    </div>
  );
};

interface StripProps {
  className?: string;
}

/** The cold→hot ramp as a thin signature strip (product surfaces only). */
export const PropensityStrip: React.FC<StripProps> = ({ className = '' }) => (
  <div
    className={`h-1 w-full ${className}`}
    style={{ background: PROPENSITY_RAMP }}
    aria-hidden="true"
  />
);

/** A single hexagon outline — EmptyState's quiet brand echo behind its icon. */
export const HexOutline: React.FC<MarkProps> = ({ size = 72, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 120 120"
    className={className}
    aria-hidden="true"
    focusable="false"
  >
    <polygon
      points={HEX_POINTS}
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
    />
  </svg>
);
